import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { api } from "@/lib/api";
import type { ParameterTimeSeriesChartResult } from "@/lib/api";
import {
  resolveChartSeriesLabel,
  resolveTimeSeriesConfigUnit,
  toParameterTimeSeriesChartResultFromModeled,
} from "@/lib/api/parameter-time-series-chart-result.utils";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import { useMapTimeseriesFetchRequests } from "@/components/map/map-control-timeseries-content";

type UseModeledSeriesChartDataParams = {
  latlng: { lat: number; lng: number };
  timeSeriesConfigs: TimeSeriesConfig[];
  /** `parameterId -> modelId` for model-sourced params; observed params are absent. */
  parameterModelIndex: Record<string, string>;
};

type ModeledSeriesChartData = {
  /** One entry per model parameter that returned data (missing series are omitted). */
  chartResults: ParameterTimeSeriesChartResult[];
  pending: boolean;
};

/**
 * Coordinate-based modeled series for the standalone `time_series` widget: for
 * each configured model parameter it picks its model's latest run and fetches
 * the series at the station coordinate.
 *
 * The multi-model sibling of {@link useModeledCoordinateTimeseriesChartData}
 * (single-model, map-click driven): a `time_series` chart can mix parameters
 * from several models, so runs are resolved per distinct model id.
 */
export function useModeledSeriesChartData({
  latlng,
  timeSeriesConfigs,
  parameterModelIndex,
}: UseModeledSeriesChartDataParams): ModeledSeriesChartData {
  const fetchRequests = useMapTimeseriesFetchRequests(timeSeriesConfigs);

  const modelRequests = useMemo(
    () =>
      fetchRequests
        .map((request) => ({
          ...request,
          modelId: parameterModelIndex[request.parameterId],
        }))
        .filter((request): request is typeof request & { modelId: string } =>
          Boolean(request.modelId),
        ),
    [fetchRequests, parameterModelIndex],
  );

  const modelIds = useMemo(
    () => [...new Set(modelRequests.map((request) => request.modelId))],
    [modelRequests],
  );

  const referenceTimes = useQueries({
    queries: modelIds.map((modelId) => ({
      queryKey: ["parameters", modelId, "model_runs"],
      queryFn: () => unwrapAxiosData(api.parameters.listModelRuns(modelId)),
    })),
    combine: (results) => ({
      byModelId: Object.fromEntries(
        modelIds.map((modelId, index) => [
          modelId,
          results[index]?.data?.[0]?.reference_time,
        ]),
      ) as Record<string, string | undefined>,
      pending: results.some((result) => result.isPending),
    }),
  });

  return useQueries({
    queries: modelRequests.map(({ parameterId, unitCode, modelId }) => {
      const referenceTime = referenceTimes.byModelId[modelId];
      return {
        queryKey: [
          "parameters",
          modelId,
          "parameters",
          "timeseries",
          referenceTime,
          latlng.lat,
          latlng.lng,
          parameterId,
          unitCode,
        ] as const,
        queryFn: async () => {
          const response = await api.parameters.getModeledParameterTimeSeriesForCoordinates(
            modelId,
            parameterId,
            referenceTime as string,
            latlng.lat,
            latlng.lng,
            unitCode,
          );
          return toParameterTimeSeriesChartResultFromModeled(response.data, {
            parameterLabel: resolveChartSeriesLabel(parameterId, timeSeriesConfigs),
            unit: resolveTimeSeriesConfigUnit(parameterId, timeSeriesConfigs),
          });
        },
        enabled: !!referenceTime,
        placeholderData: keepPreviousData,
        retry: false,
      };
    }),
    combine: (results) => ({
      chartResults: results
        .map((result) => result.data)
        .filter((data): data is ParameterTimeSeriesChartResult => !!data),
      pending: referenceTimes.pending || results.some((result) => result.isPending),
    }),
  });
}
